import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import {
    DashboardItemComponentType,
    DashboardItemFactory,
    IDashboardItemFactoryOptions,
} from 'src/app/utils/dashboard-item';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { pivotTable } from 'src/app/utils/pivot';
import {
    IDateRangePickerDashboardItem,
    RelativeDateRangeBound,
} from './date-picker.component';

export interface IDataRangePickerData {
    value: string[];
}

export const allRangePresetIds = [
    'lifetime',
    'today',
    'yesterday',
    'last 7 days',
    'last 14 days',
    'last 30 days',
    'last 60 days',
    'last 90 days',
    'last 180 days',
    'last 365 days',
    'last 540 days',
    'last 720 days',
    'custom',
] as const; // convert to readonly tuple of string literals

// equivalent to "today" | "last 7 days" | ... | "last year"
export type DateRangePresetId = (typeof allRangePresetIds)[number];

export const defaultRangePresetIds: DateRangePresetId[] = [
    'lifetime',
    'yesterday',
    'last 7 days',
    'last 30 days',
    'last 60 days',
    'last 90 days',
    'last 180 days',
    'last 365 days',
    'last 540 days',
    'last 720 days',
    'custom',
];

export const DateSplitAggregates = [
    'day',
    'week',
    'month',
    'quarter',
    'year',
    'total',
] as const;

export type DateSplitAggregate = (typeof DateSplitAggregates)[number];

export interface CustomPresetValue {
    preset: 'custom';
    start?: Date;
    end?: Date;
    relativeStart?: RelativeDateRangeBound;
    relativeEnd?: RelativeDateRangeBound;
    splitAggregate: DateSplitAggregate;
}

export type NamedPresetValue = {
    preset: Exclude<DateRangePresetId, 'custom'>;
    splitAggregate: DateSplitAggregate;
};

export interface DateRangePickerDashboardItemFactoryOptions extends IDashboardItemFactoryOptions {
    createdInstanceId: string;
    datasourceColumnId: DatasourceColumnId;
    /** Whether to show control checkbox and split range select dropdown */
    splitable?: boolean;
    /** Control checkbox is not shown, but split range select dropdown is shown. Assumes that split is always applied */
    forcedSplitable?: boolean;
    /** Controls the available split aggregates in the split range select dropdown */
    spltAggregates?: DateSplitAggregate[];
    /** Presets to show in the range select dropdown */
    rangePresets?: DateRangePresetId[];
    /** Label for the filter
     * @default Label defined in
     */
    datasourceColumnLabel?: string;
}

export class DateRangePickerDashboardItemFactory extends DashboardItemFactory<
    IDateRangePickerDashboardItem,
    DateRangePickerDashboardItemFactoryOptions
> {
    override FACTORY_CLASS: string = 'FilterCategoryDashboardItemFactory';
    override COMPONENT_CLASS: DashboardItemComponentType =
        'DateRangeFilterDashboardItem';

    override getInstance(options: DateRangePickerDashboardItemFactoryOptions) {
        // default splitable to false
        // options.splitable = options.splitable ?? false;

        const instance: IDateRangePickerDashboardItem = {
            instanceId: options.createdInstanceId,
            datasourceColumnId: options.datasourceColumnId,
            datasourceColumnLabel: options.datasourceColumnLabel,
            datasourceColumn: {
                id: options.datasourceColumnId,
                state: {
                    value: { preset: 'lifetime', splitAggregate: 'day' },
                    isFilterApplied: false,
                    isSplitApplied: false,
                },
            },
            options: {
                splitable: options.splitable,
                forcedSplitable: options.forcedSplitable,
                spltAggregates: options.spltAggregates,
            },
            dashboardItem: {
                id: options.datasourceColumnId,
                initialState: {
                    value: [],
                    split: false,
                    isFilterApplied: false,
                    isSplitApplied: false,
                },
            },
            component: this.COMPONENT_CLASS,
            composeData: (data: any[], context?: IQueryContext) => {
                const pivotResult = pivotTable<any>(data);
                const selectOptions =
                    pivotResult[options.datasourceColumnId.replace('.', '_')];
                return { value: selectOptions };
            },
        };

        if (options.rangePresets) {
            instance.options.rangePresets = options.rangePresets;
        }

        return instance;
    }
}
